import { BrowserEventName, PseudoEventName, TableauMouseButton } from '../gen-src/preslayer/Enums';
import { BrowserSupport } from '@tableau/browser-support';
import { Discrete } from './gesture/Discrete';
import { DomUtil } from './utility/DomUtil';
import { Drag } from './gesture/Drag';
import { EventHandleSpec } from './EventHandleSpec';
import { Hover } from './gesture/Hover';
import { IDisposable } from './definition/IDisposable';
import { IMouseCaptureEventHandler } from './definition/IMouseCaptureEventHandler';
import { Logger } from './utility/VizclientUtils';
import { MiscUtil } from './utility/MiscUtil';
import { MouseEventUtil } from './utility/MouseEventUtil';
import { NormalizedEvent } from './event/NormalizedEvent';
import { Pinch } from './gesture/Pinch';
import { TableauEventHandler } from './TableauEventHandler';

/**
* The responsibility of this class is to inspect browser events and forward them to the gesture instances (that this class manages).
*/
class EventRecognizer implements IDisposable, IMouseCaptureEventHandler {

  // these gestures are create as needed by caller
  private drag: Drag;

  private discrete: Discrete;
  private pinch: Pinch;
  private hover: Hover;

  private readonly usingHover: boolean;
  private readonly domElement: HTMLElement;

  // TODO: Move the mouse behavior handling into a separate class.
  // All the variables that represent mouse state should then move to this new class.
  private mousedown: boolean = false;
  private lastMouseButtonPressed: TableauMouseButton;
  private handlingContextMenu: boolean = false;
  private static globalMouseButton: TableauMouseButton = TableauMouseButton.None; // the current state of the mouse buttons

  public constructor(config: EventHandleSpec) {
    this.usingHover = config.IsUsingHover();
    this.domElement = config.DomElement;

    // these Recognizers are used by all
    this.drag = new Drag(config, this);
    this.discrete = new Discrete(config);
    this.pinch = new Pinch(config);
    if (this.usingHover) {
      this.hover = new Hover(config);
    }
  }

  /**
   * Gets or sets globally tracked mouse button state, this is useful for knowing when there was a left mouse button
   *    up somewhere else.
   * Note: The property name implies and the implementation is capable of tracking left/middle/right buttons.
   * However, the way the mouseup (startEvent) is implemented below only the left mouse button is tracked. This is
   * because it seems the intent here is to ignore non-left-mouse clicks.
  */
  public static get CurrentGlobalMouseButton(): TableauMouseButton {
    return EventRecognizer.globalMouseButton;
  }

  public static set CurrentGlobalMouseButton(value: TableauMouseButton) {
    if (EventRecognizer.globalMouseButton !== value) {
      EventRecognizer.globalMouseButton = value;
      Logger.Debug('GlobalMouseButton=' + EventRecognizer.globalMouseButton);
    }
  }

  public suppressHover(): void {
    this.hover.suppressHover();
  }

  public unsuppressHover(): void {
    this.hover.unsuppressHover();
  }

  public CancelHover(): void {
    this.hover.Cancel();
  }

  public HandleEvent(e: NormalizedEvent): void {
    // if there was a left mouse down in this handler but the mouse was moved and the mouse
    // up wasn't recorded our local mousedown state will be incorrect. We need to know
    // if that mouseup occurred so we know whether to enable hovering.
    if (this.mousedown
      && (EventRecognizer.CurrentGlobalMouseButton !== TableauMouseButton.Left
        && EventRecognizer.CurrentGlobalMouseButton !== TableauMouseButton.Right)) {
      this.mousedown = false;
    }

    if (e.IsScrollEvent) {
      // noop
    } else if (e.IsOutEvent) {
      if (this.usingHover) {
        this.hover.Out(e);
      }
    } else if (e.IsStartEvent) {
      this.HandleStartEvent(e);
    } else if (e.IsMoveEvent) {
      this.HandleMoveEvent(e);
    } else { // endEvent, cancelEvent
      this.HandleEndCancelEvent(e);
    }
  }

  private HandleStartEvent(e: NormalizedEvent): void {
    this.handlingContextMenu = false;
    this.lastMouseButtonPressed = MouseEventUtil.GetMouseButtonEnum(e.originalEvent);
    if (e.IsMouseEvent) {
      this.mousedown = true;

      // globally maintain the state of the mouse buttons
      // when in mouse mode startEvent corresponds to a mousedown
      EventRecognizer.CurrentGlobalMouseButton = this.lastMouseButtonPressed;
    }
    // Though no hover with touch, a touch start event should have same effect as a mouse start event
    this.HandleStart(e);
  }

  private HandleMoveEvent(e: NormalizedEvent): void {
    if (this.usingHover) {
      this.hover.Move(e);
    }
    if (e.IsMouseEvent) {
      // bugzid: 39261
      if (!this.mousedown) {
        return;
      }
    }
    // order shouldn't matter
    this.discrete.Move(e);
    this.drag.Move(e);
    if (!MiscUtil.IsNullOrUndefined(this.pinch)) {
      this.pinch.Move(e);
    }
  }

  private HandleEndCancelEvent(e: NormalizedEvent): void {
    // globally maintain the state of the mouse buttons
    // when in mouse mode endEvent corresponds to a mouseup
    EventRecognizer.CurrentGlobalMouseButton = TableauMouseButton.None;

    // Though no hover with touch, a touch end event should have same effect as a mouse end event
    if (this.usingHover) {
      this.hover.End(e);
    }

    // order matters
    if (!MiscUtil.IsNullOrUndefined(this.pinch)) {
      this.pinch.End(e);
    }
    this.drag.End(e);

    // track whether we are handling a right-click event sequence
    if (e.IsMouseEvent) {
      this.mousedown = false;

      // Special handling for contextmenu (right-click and/or ctrl+left-click on Mac) event:
      // on Mac we get contextmenu before the mouseup event, so we ignore the trailing mouseup event
      if (e.IsRightClickEvent || this.lastMouseButtonPressed === TableauMouseButton.Right) {
        this.handlingContextMenu = true;
      }

      // on Windows we get mouseup event before the contextmenu event, so we ignore mouse events until we get contextmenu event.
      if ((BrowserSupport.IsMac() && this.handlingContextMenu &&
        e.type === BrowserEventName.MouseUp) || (this.handlingContextMenu && !e.IsRightClickEvent)) {
        return;
      }
    }
    this.discrete.End(e);
  }

  public CancelAllGestures(): void {
    // cancel all current gestures
    if (!MiscUtil.IsNullOrUndefined(this.drag)) {
      this.drag.Cancel();
    }
    if (!MiscUtil.IsNullOrUndefined(this.discrete)) {
      this.discrete.Cancel();
    }
    if (!MiscUtil.IsNullOrUndefined(this.pinch)) {
      this.pinch.Cancel();
    }
    if (!MiscUtil.IsNullOrUndefined(this.hover)) {
      this.hover.Cancel();
    }
  }

  public UpdateAllGestures(newConfig: EventHandleSpec): void {
    // cancel all current gestures
    if (!MiscUtil.IsNullOrUndefined(this.drag)) {
      this.drag.Update(newConfig);
    }
    if (!MiscUtil.IsNullOrUndefined(this.discrete)) {
      this.discrete.Update(newConfig);
    }
    if (!MiscUtil.IsNullOrUndefined(this.pinch)) {
      this.pinch.Update(newConfig);
    }
    if (!MiscUtil.IsNullOrUndefined(this.hover)) {
      this.hover.Update(newConfig);
    }
  }

  // press drag is a modal gesture. Once press happens, then the callbacks to drag become
  // pressDrag callbacks, and pinch become doublePressDrag callbacks. Callbacks revert to
  // originals when the gesture ends
  public SetupPressDrag(oldConfig: EventHandleSpec): void {
    let usingPressDrag: boolean = oldConfig.UsingPressDrag();
    if (!usingPressDrag) {
      return;
    }
    let press: (_: NormalizedEvent) => void = (e: NormalizedEvent) => {
      if (oldConfig.IsHandling(PseudoEventName.Press)) {
        let callback: (_: NormalizedEvent) => void = TableauEventHandler.GetHandler(oldConfig, PseudoEventName.Press);
        callback(e);
      }
      let newConfig: EventHandleSpec = new EventHandleSpec();
      newConfig.SetHandler(PseudoEventName.DragStart,
        // tslint:disable-next-line:align
        TableauEventHandler.GetHandler(oldConfig, PseudoEventName.PressDragStart));
      newConfig.SetHandler(PseudoEventName.DragMove,
        // tslint:disable-next-line:align
        TableauEventHandler.GetHandler(oldConfig, PseudoEventName.PressDragMove));
      newConfig.SetHandler(PseudoEventName.DragEnd,
        // tslint:disable-next-line:align
        TableauEventHandler.GetHandler(oldConfig, PseudoEventName.PressDragEnd));
      newConfig.SetHandler(PseudoEventName.PinchStart,
        // tslint:disable-next-line:align
        TableauEventHandler.GetHandler(oldConfig, PseudoEventName.DoublePressDragStart));
      newConfig.SetHandler(PseudoEventName.PinchMove,
        // tslint:disable-next-line:align
        TableauEventHandler.GetHandler(oldConfig, PseudoEventName.DoublePressDragMove));
      newConfig.SetHandler(PseudoEventName.PinchEnd,
        // tslint:disable-next-line:align
        TableauEventHandler.GetHandler(oldConfig, PseudoEventName.DoublePressDragEnd));
      this.drag.Update(newConfig);
      if (!MiscUtil.IsNullOrUndefined(this.pinch)) {
        this.pinch.Update(newConfig);
      }
    };
    let lastTouch: (_: NormalizedEvent) => void = (e: NormalizedEvent) => {
      if (oldConfig.IsHandling(PseudoEventName.LastTouch)) {
        let callback: (_: NormalizedEvent) => void = TableauEventHandler.GetHandler(oldConfig, PseudoEventName.LastTouch);
        callback(e);
      }
      this.drag.Update(oldConfig);
      if (!MiscUtil.IsNullOrUndefined(this.pinch)) {
        this.pinch.Update(oldConfig);
      }
    };
    let pressConfig: EventHandleSpec = new EventHandleSpec();
    pressConfig.SetHandler(PseudoEventName.Press, press);
    pressConfig.SetHandler(PseudoEventName.LastTouch, lastTouch);
    this.discrete.Update(pressConfig);
  }

  private HandleStart(e: NormalizedEvent): void {
    if (this.usingHover) {
      this.hover.Start(e);
    }

    // order matters
    this.discrete.Start(e);
    this.drag.Start(e);
    if (!MiscUtil.IsNullOrUndefined(this.pinch)) {
      this.pinch.Start(e);
    }
  }

  public dispose(): void {
    if (!MiscUtil.IsNullOrUndefined(this.discrete)) {
      this.discrete.dispose();
    }
    if (!MiscUtil.IsNullOrUndefined(this.drag)) {
      this.drag.dispose();
    }
    if (!MiscUtil.IsNullOrUndefined(this.hover)) {
      this.hover.dispose();
    }
    if (!MiscUtil.IsNullOrUndefined(this.pinch)) {
      this.pinch.dispose();
    }
  }

  /**
   * Called when the mouse is moved while mouse events are being captured
   */
  public HandleCapturedMouseMove(e: TouchEvent): void {
    if (this.IsCapturedEventOutsideOfTarget(e)) {
      let normalizedEvent = NormalizedEvent.Create(e, this.domElement);
      this.HandleMoveEvent(normalizedEvent);
    }
  }

  /**
   * Called when mouseup occurs or on mouseout if we're in an iFrame and don't support setCapture
   */
  public HandleCapturedMouseComplete(e: TouchEvent): void {
    // Always handle captured mouseout events as end events, regardless of whether the event will naturally bubble up
    // to the target. We don't normally handle mouseout events as end events, however when MouseCapture sends them it
    // indicates that the gesture needs to be ended prematurely because we don't expect to receive any further events
    // due to the gesture leaving frame context.
    const isMouseOut = e.type === BrowserEventName.MouseOut;

    if (this.IsCapturedEventOutsideOfTarget(e) || isMouseOut) {
      let normalizedEvent = NormalizedEvent.Create(e, this.domElement);
      this.HandleEndCancelEvent(normalizedEvent);
    }
  }

  /**
   * Returns true if the event will not naturally bubble to the capturing element via normal browser event propagation.
   */
  private IsCapturedEventOutsideOfTarget(e: TouchEvent): boolean {
    return this.domElement !== e.target
      && !DomUtil.IsAncestorOf(this.domElement, e.target as HTMLElement);
  }
}

export { EventRecognizer };
